import type { IGhIconName } from '../gpthelp-host-contract';
import { cardTextLength, clampText } from '../cards/card-text';
import type { ICardFormat } from '../cards/card-text';
import type { ICartadigitalAdminHostToolDeps } from '../tools/tool-types';

/** What a request to the support team is about (`support_handoff.topic`, plan §8 R4). */
export const SUPPORT_TOPICS = [
  'account',
  'login',
  'store',
  'billing',
  'bug',
  'other',
] as const;
export type TSupportTopic = typeof SUPPORT_TOPICS[number];

/** Characters of the request the model writes (`support_handoff.summary`). */
export const SUPPORT_SUMMARY_MIN = 5;
export const SUPPORT_SUMMARY_MAX = 300;
/** Characters of the message: the support card's text (host contract §3.3). */
export const SUPPORT_MESSAGE_MAX = 600;
/** Characters of the link that opens the message. */
export const SUPPORT_URL_MAX = 2000;

/**
 * A WhatsApp link: it shows WhatsApp's icon and takes the message as
 * `text=`. One pattern for both, so the icon never promises a written
 * message the link does not carry.
 */
export const WHATSAPP_LINK = /^https:\/\/(wa\.me|(api\.|www\.)?whatsapp\.com)([/?#]|$)/i;

/**
 * Who writes, from where: the member's own name and the store's and
 * branch's names and slug. Never emails, phones or ids.
 */
export interface ISupportSender {
  user: string;
  store: string;
  slug: string;
  branch: string;
}

export interface ISupportMessageInput {
  fmt: ICardFormat;
  sender: ISupportSender;
  topic: TSupportTopic;
  /** The request in the user's words. */
  summary: string;
  /** The support link of the current config (https or mailto:). */
  supportUrl: string;
}

export interface ISupportMessage {
  /** The request as the message carries it (cut to fit). */
  summary: string;
  /** What the card shows and the link sends. */
  message: string;
  /** The link the card's button opens. */
  url: string;
}

export function isSupportTopic(value: unknown): value is TSupportTopic {
  return SUPPORT_TOPICS.some((topic) => topic === value);
}

/** The member and the store and branch they write from. */
export function supportSenderOf(
  deps: Pick<ICartadigitalAdminHostToolDeps, 'authStore' | 'restaurantStore'>
): ISupportSender {
  const restaurant = deps.restaurantStore.restaurant;
  return {
    user: deps.authStore.user?.name ?? '',
    store: restaurant?.name ?? '',
    slug: restaurant?.stringId ?? '',
    branch: deps.restaurantStore.selectedBranch?.name ?? '',
  };
}

/** WhatsApp's icon for a WhatsApp link, a chat bubble for any other (an email, a help page). */
export function supportIconOf(url: string): IGhIconName {
  return WHATSAPP_LINK.test(url) ? 'whatsapp' : 'chat';
}

/**
 * The link that opens `message`: a WhatsApp chat with the text written, an
 * email with its subject and body, any other page as it is (the message is
 * on the card to copy).
 */
export function supportLinkOf(
  supportUrl: string,
  message: string,
  subject: string
): string {
  const separator = supportUrl.includes('?') ? '&' : '?';
  if (WHATSAPP_LINK.test(supportUrl))
    return `${supportUrl}${separator}text=${encodeURIComponent(message)}`;
  if (supportUrl.startsWith('mailto:'))
    return `${supportUrl}${separator}subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(message)}`;
  return supportUrl;
}

/** The request, then shorter and shorter cuts of it («…» at the end). */
function summaryCuts(summary: string): string[] {
  const length = Array.from(summary).length;
  const cuts = [summary];
  for (let keep = length - 1; keep > 0; keep -= 1) {
    cuts.push(clampText(summary, keep));
  }
  return cuts;
}

/**
 * The support message and the link that opens it. The request is cut until
 * the message fits the card (600 characters) and the link 2,000, so the card
 * shows exactly what the link sends. Built again from the cut request and
 * the same support link, it gives the same message and link (the card's
 * button relies on it). When even the shortest cut does not fit (very long
 * names, or a support link near 2,000 characters), the card keeps the
 * message and the button opens the support link alone.
 */
export function buildSupportMessage(
  input: ISupportMessageInput
): ISupportMessage {
  const { fmt, sender, supportUrl } = input;
  const topic = fmt.t(`assistant.support.topic.${input.topic}`);
  const subject = fmt.t('assistant.support.subject', { store: sender.store });
  const textWith = (summary: string) =>
    fmt.t('assistant.support.message', {
      user: sender.user,
      topic,
      store: sender.store,
      slug: sender.slug,
      branch: sender.branch,
      summary,
    });

  const cuts = summaryCuts(clampText(input.summary, SUPPORT_SUMMARY_MAX));
  const onCard = cuts.filter(
    (cut) => cardTextLength(textWith(cut)) <= SUPPORT_MESSAGE_MAX
  );
  for (const summary of onCard) {
    // Whitespace normalized as on the card; nothing is cut (it fits).
    const message = clampText(textWith(summary), SUPPORT_MESSAGE_MAX);
    const url = supportLinkOf(supportUrl, message, subject);
    if (url.length <= SUPPORT_URL_MAX) return { summary, message, url };
  }
  const summary = onCard[0] ?? cuts[cuts.length - 1];
  return {
    summary,
    message: clampText(textWith(summary), SUPPORT_MESSAGE_MAX),
    url: supportUrl,
  };
}
